import { useState, useMemo } from 'react';
import { feats } from '../data/feats';
import { FEAT_CATEGORIES } from '../types';
import type { Feat, FeatCategory } from '../types';
import { FeatCard } from '../components/FeatCard';
import { CardListPage, type CardListPageConfig } from '../components/CardListPage';
import { useImport } from '../context/useImport';
import './FeatsPage.css';

type SortOption = 'name' | 'category';

function FeatListCard({ entity, isSelected, onToggle }: { entity: Feat; isSelected: boolean; onToggle: (id: string) => void }) {
  return <FeatCard feat={entity} isSelected={isSelected} onToggle={onToggle} />;
}

export function FeatsPage() {
  const [selectedCategory, setSelectedCategory] = useState<FeatCategory | 'All'>('All');
  const [sortBy, setSortBy] = useState<SortOption>('category');
  const [importedOnly, setImportedOnly] = useState(false);

  const {
    customSpells,
    customItems,
    customClasses,
    customFeats,
    multiTabImportState,
    importMultiTab,
    importMultipleUrls,
    clearMultiTabImportStatus,
    clearCustomSpells,
    clearCustomItems,
    clearCustomClasses,
    clearCustomFeats,
    combinedTemplateUrl,
  } = useImport();

  const allFeats = useMemo(() => [...feats, ...customFeats], [customFeats]);
  const visibleFeats = useMemo(
    () => (importedOnly ? customFeats : allFeats),
    [importedOnly, customFeats, allFeats],
  );

  const categoryOrder: FeatCategory[] = FEAT_CATEGORIES;

  const filteredFeats = useMemo(() => {
    return visibleFeats
      .filter((feat) => {
        if (selectedCategory !== 'All' && feat.category !== selectedCategory) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'category') {
          const catDiff = categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category);
          if (catDiff !== 0) return catDiff;
          return a.name.localeCompare(b.name);
        }
        return a.name.localeCompare(b.name);
      });
  }, [selectedCategory, sortBy, visibleFeats, categoryOrder]);

  const renderFilters = () => (
    <>
      <div className="filter-row">
        <div className="filter-group">
          <label>CATEGORY:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as FeatCategory | 'All')}
            className="filter-select"
          >
            <option value="All">All Categories</option>
            {FEAT_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>SORT:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="filter-select"
          >
            <option value="category">Category</option>
            <option value="name">A-Z</option>
          </select>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group imported-only-group">
          <label htmlFor="feats-imported-only" className="imported-only-label">
            <input
              id="feats-imported-only"
              type="checkbox"
              checked={importedOnly}
              onChange={(e) => setImportedOnly(e.target.checked)}
              className="imported-only-checkbox"
            />
            <span>IMPORTED ONLY</span>
          </label>
        </div>
      </div>
    </>
  );

  const config: CardListPageConfig<Feat> = {
    variant: 'feats',
    pageClassName: 'feats-page',
    listClassName: 'feats-list',
    documentTitle: 'Feat Cards',
    searchPlaceholder: 'Search feats...',
    emptyState: {
      icon: '⚔️',
      title: 'Feat Cards',
      description: 'Select feats from the list to preview them here',
      instructions: [
        '1. Use the filters to find feats by category',
        '2. Click on feats to add them to your selection',
        '3. Download your cards when ready to print',
      ],
    },
    import: {
      onImport: importMultiTab,
      onImportMultiple: importMultipleUrls,
      state: multiTabImportState,
      onClearStatus: clearMultiTabImportStatus,
      spellsCount: customSpells.length,
      itemsCount: customItems.length,
      classesCount: customClasses.length,
      featsCount: customFeats.length,
      onClearSpells: clearCustomSpells,
      onClearItems: clearCustomItems,
      onClearClasses: clearCustomClasses,
      onClearFeats: clearCustomFeats,
      combinedTemplateUrl,
    },
    ListCard: FeatListCard,
    renderFilters,
  };

  return (
    <CardListPage
      allEntities={visibleFeats}
      filteredEntities={filteredFeats}
      config={config}
    />
  );
}
